import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DprCard } from '@dpr/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConfirmDialogComponent } from '@shared/components';
import {
  dynamicTextPlaceholder,
  FileUploadType,
  StaticMessage,
  UtcDateTimeOptions,
} from '@shared/enums';
import { Attachment } from '@shared/models/dpr';
import { DprTitlePipe } from '@shared/pipes/dpr-title.pipe';
import { FileService } from '@shared/services/files';
import { Constants, getStaticMessage, toUTCDate } from '@shared/utils';
import { catchError, mergeMap, Observable, of } from 'rxjs';

@UntilDestroy()
@Injectable()
export class DprCardService {
  constructor(
    private datePipe: DatePipe,
    private fileService: FileService,
    private dialog: MatDialog,
    private dprTitlePipe: DprTitlePipe
  ) {}

  formatDprDate(dateTime: Date, format?: string): string {
    const utcDate = toUTCDate(dateTime, UtcDateTimeOptions.UtcDateWithoutTime);
    return this.datePipe.transform(utcDate, format ?? 'yyyy-MM-dd');
  }

  uploadSignedReport(
    files: File[],
    clientId: string,
    data: DprCard
  ): Observable<Attachment> {
    if (!files?.length) {
      return of(null);
    }

    const formattedDprDisplayId = this.dprTitlePipe.transform(data?.displayId);

    return this.fileService
      .uploadFile(files[0], FileUploadType.Report, clientId)
      .pipe(
        mergeMap((response) => {
          const attachment: Attachment = {
            name: response.file.name,
            type: response.file.type,
            size: response.file.size,
            isPrintInReport: false,
            externalUrl: response.url,
          };

          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            ...Constants.DialogOptions,
            data: {
              extra: `#${formattedDprDisplayId}`,
              title: $localize`:@@dpr.dprCardDetails.titleUploadSignedReport:Upload Signed Report`,
              message: $localize`:@@dpr.dprCardDetails.txtProceedWithUploading:Proceed with uploading the signed report?`,
              buttonLabel: getStaticMessage(StaticMessage.ContinueButton),
            },
          });

          return dialogRef.afterClosed().pipe(
            untilDestroyed(this),
            mergeMap((result) => {
              return result ? of(attachment) : of(null);
            })
          );
        }),
        catchError(() => {
          this.dialog.open(ConfirmDialogComponent, {
            ...Constants.DialogOptions,
            data: {
              extra: `#${formattedDprDisplayId}`,
              title: getStaticMessage(StaticMessage.UploadErrorTitle),
              message: $localize`:@@dpr.dprCardDetails.txtErrorWhileSavingFileTryAgain:An error has occurred while saving your file, please try again or contact your administrator.`,
              buttonLabel: getStaticMessage(StaticMessage.OkButton),
              hideCancelButton: true,
            },
          });
          return of(null);
        })
      );
  }

  getModalTitle(
    displayId: number,
    dprDate: Date,
    dprDetailRevision: number
  ): string {
    const title = $localize`:@@dpr.dprCardService.titleModal:DPR #(---), (---), Revision (---)`;
    return title
      .replace(dynamicTextPlaceholder, this.dprTitlePipe.transform(displayId))
      .replace(
        dynamicTextPlaceholder,
        this.datePipe.transform(
          toUTCDate(new Date(dprDate), UtcDateTimeOptions.UtcDateWithoutTime),
          'MMM dd'
        )
      )
      .replace(dynamicTextPlaceholder, dprDetailRevision.toString());
  }
}
